import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { usePrivateChatSocket } from '../contexts/PrivateChatSocketContext';
import { useTrial } from '../contexts/TrialContext';
import { translateMessage, translateMessageAsync } from '../utils/chatTranslation';
import '../styles/FriendChat.css';
import logger from '../utils/logger';

// Helper function to check if a string looks like encoded/binary data
const looksLikeEncodedData = (str) => {
  if (!str || typeof str !== 'string') return false;
  if (str.startsWith('data:')) return true;
  if (str.length > 15 && !str.includes(' ')) {
    if (/^[A-Za-z0-9+/=_-]+$/.test(str)) return true;
  }
  if (str.length > 10 && /[+/=]{2,}/.test(str)) return true;
  if (str.length > 12 && /^[a-z]+[A-Z0-9+/]+/.test(str)) return true;
  return false;
};

// Helper function to sanitize message display
const sanitizeMessageDisplay = (message) => {
  if (!message) return '';
  if (looksLikeEncodedData(message)) return '📎 [Attachment]';
  return message;
};

// Helper function to get display name
const getDisplayName = (friend) => {
  if (!friend) return 'Unknown';
  const name = friend.name;
  if (name && !looksLikeEncodedData(name)) return name;
  if (friend.email) {
    const emailName = friend.email.split('@')[0];
    return emailName.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return 'Friend';
};

// Helper function to get safe avatar
const getSafeAvatar = (avatar) => {
  if (!avatar) return '👤';
  if (looksLikeEncodedData(avatar)) return '👤';
  if (avatar.length <= 4) return avatar;
  if (avatar.length > 10 && !avatar.startsWith('http')) return '👤';
  return avatar;
};

const FriendChat = ({ isOpen, onClose, onUnreadCountChange, addGlobalNotification }) => {
  // Main view: 'messages' | 'friends' | 'chat'
  const [mainTab, setMainTab] = useState('messages');
  // Friends sub-tab: 'list' | 'requests' | 'search'
  const [friendsSubTab, setFriendsSubTab] = useState('list');
  
  const [conversations, setConversations] = useState([]);
  const [friendsList, setFriendsList] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({ received: [], sent: [] });
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteConvConfirm, setDeleteConvConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
  const [asyncTranslations, setAsyncTranslations] = useState({});
  
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const selectedFriendRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const searchTimeoutRef = useRef(null);

  const { user, token } = useAuth();
  const { language } = useTranslation();
  const { connected, addMessageHandler } = usePrivateChatSocket();
  const { hasActiveAccess } = useTrial();
  const hasPremiumChat = hasActiveAccess();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch AI translation for a message
  const fetchTranslation = useCallback(async (msgKey, text) => {
    if (!text || asyncTranslations[msgKey]) return;
    
    try {
      const result = await translateMessageAsync(text, language);
      if (result.translated) {
        setAsyncTranslations(prev => ({
          ...prev,
          [msgKey]: result.translated
        }));
      }
    } catch (err) {
      logger.error('Translation fetch error:', err);
    }
  }, [language, asyncTranslations]);

  // Keep refs in sync
  useEffect(() => {
    selectedFriendRef.current = selectedFriend;
  }, [selectedFriend]);
  
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
      const totalUnread = (response.data.conversations || []).reduce(
        (sum, conv) => sum + (conv.unread_count || 0), 0
      );
      if (onUnreadCountChange) onUnreadCountChange(totalUnread);
    } catch (error) {
      logger.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, onUnreadCountChange]);

  // Load friends list
  const loadFriends = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendsList(response.data.friends || []);
    } catch (error) {
      logger.error('Failed to load friends:', error);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  // Load pending friend requests
  const loadPendingRequests = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/friends/requests/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequests({
        received: response.data.received || [],
        sent: response.data.sent || []
      });
    } catch (error) {
      logger.error('Failed to load pending requests:', error);
    }
  }, [token, API_URL]);

  // Search users
  const searchUsers = useCallback(async (query) => {
    if (!token || !query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/friends/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.users || []);
    } catch (error) {
      logger.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [token, API_URL]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchQuery.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchUsers]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && token) {
      loadConversations();
      loadFriends();
      loadPendingRequests();
    }
  }, [isOpen, token, loadConversations, loadFriends, loadPendingRequests]);

  // Handle WebSocket messages
  useEffect(() => {
    if (!connected || !addMessageHandler) return;
    
    const handleMessage = (data) => {
      if (data.type === 'new_message') {
        if (selectedFriendRef.current?.user_id === data.sender_id) {
          setMessages(prev => [...prev, {
            message_id: data.message_id || Date.now(),
            sender_id: data.sender_id,
            message: data.message,
            created_at: data.timestamp || new Date().toISOString()
          }]);
        }
        // Refresh conversations
        loadConversations();
        
        // Show notification if chat is not open or different friend selected
        if (!isOpenRef.current || selectedFriendRef.current?.user_id !== data.sender_id) {
          if (addGlobalNotification) {
            addGlobalNotification(
              'chat_message',
              null,
              data.sender_name || 'Friend',
              `${data.sender_name || 'Friend'}: ${sanitizeMessageDisplay(data.message)?.substring(0, 50)}`
            );
          }
        }
      }
    };
    
    const unsubscribe = addMessageHandler(handleMessage);
    return () => unsubscribe();
  }, [connected, addMessageHandler, loadConversations, addGlobalNotification]);

  // Load messages for a friend
  const loadMessages = async (friendId) => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/chat/messages/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      // Mark as read
      await axios.post(`${API_URL}/api/chat/read/${friendId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadConversations();
    } catch (error) {
      logger.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Open chat with friend
  const openChat = (friend) => {
    setSelectedFriend(friend);
    setMainTab('chat');
    loadMessages(friend.user_id);
  };

  // Back to messages
  const backToMessages = () => {
    setMainTab('messages');
    setSelectedFriend(null);
    setMessages([]);
    loadConversations();
  };

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedFriend || sending) return;
    
    const messageText = messageInput.trim();
    setMessageInput('');
    setSending(true);
    
    // Optimistic update with temp ID
    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      message_id: tempId,
      sender_id: user?.user_id,
      message: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      const response = await axios.post(`${API_URL}/api/chat/send`, {
        receiver_id: selectedFriend.user_id,
        message: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the temp message with the real message_id from server
      if (response.data.message_id) {
        setMessages(prev => prev.map(m => 
          m.message_id === tempId 
            ? { ...m, message_id: response.data.message_id }
            : m
        ));
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      setMessageInput(messageText);
    } finally {
      setSending(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMessages(prev => prev.filter(m => m.message_id !== messageId));
        setDeleteConfirm(null);
      } else {
        logger.error('Delete failed:', response.data);
        alert('Failed to delete message: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      logger.error('Failed to delete message:', error);
      alert('Failed to delete message: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (friendId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/chat/conversations/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.friend.user_id !== friendId));
      setDeleteConvConfirm(null);
    } catch (error) {
      logger.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Send friend request
  const sendFriendRequest = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/friends/request/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh search results
      if (searchQuery) searchUsers(searchQuery);
      loadPendingRequests();
    } catch (error) {
      logger.error('Failed to send friend request:', error);
      alert(error.response?.data?.detail || 'Failed to send friend request');
    } finally {
      setActionLoading(null);
    }
  };

  // Accept friend request
  const acceptFriendRequest = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/friends/accept/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadFriends();
      loadPendingRequests();
    } catch (error) {
      logger.error('Failed to accept friend request:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Decline friend request
  const declineFriendRequest = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/friends/decline/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadPendingRequests();
    } catch (error) {
      logger.error('Failed to decline friend request:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Cancel sent friend request
  const cancelFriendRequest = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.delete(`${API_URL}/api/friends/request/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadPendingRequests();
    } catch (error) {
      logger.error('Failed to cancel friend request:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get total pending requests count
  const pendingCount = pendingRequests.received.length;

  if (!isOpen) return null;

  if (!hasPremiumChat) {
    return (
      <div className="friend-chat-overlay" onClick={onClose}>
        <div className="friend-chat-container redesigned" onClick={(e) => e.stopPropagation()}>
          <div className="friend-chat-header">
            <h2>💬 Social</h2>
            <div className="header-actions">
              <button className="close-button" onClick={onClose} data-testid="close-social">×</button>
            </div>
          </div>
          <div className="premium-chat-lock" data-testid="premium-chat-lock">
            <div className="premium-lock-icon">💬</div>
            <h3>Private Chat & Friends</h3>
            <p>Connect with friends, send messages, and build your chess network</p>
            <div className="premium-lock-features">
              <div className="lock-feature">Add & manage friends</div>
              <div className="lock-feature">Private messaging</div>
              <div className="lock-feature">AI-translated chat</div>
              <div className="lock-feature">Challenge friends to games</div>
            </div>
            <div className="premium-lock-cta">
              <span className="premium-price">$4.99<small>/month</small></span>
              <span className="premium-label">Full Access</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="friend-chat-overlay" onClick={onClose}>
      <div className="friend-chat-container redesigned" onClick={(e) => e.stopPropagation()}>
        
        {/* Header with close button */}
        <div className="friend-chat-header">
          {mainTab === 'chat' ? (
            <>
              <button className="back-button" onClick={backToMessages} data-testid="back-to-messages">
                ←
              </button>
              <div className="chat-header-info">
                <span className="chat-avatar">{getSafeAvatar(selectedFriend?.avatar)}</span>
                <span className="chat-name">{getDisplayName(selectedFriend)}</span>
              </div>
            </>
          ) : (
            <h2>💬 Social</h2>
          )}
          <div className="header-actions">
            {!connected && <span className="connection-status">Connecting...</span>}
            <button className="close-button" onClick={onClose} data-testid="close-social">×</button>
          </div>
        </div>

        {/* Tab Bar - Only show when not in chat view */}
        {mainTab !== 'chat' && (
          <div className="main-tab-bar" data-testid="main-tab-bar">
            <button
              className={`main-tab ${mainTab === 'messages' ? 'active' : ''}`}
              onClick={() => setMainTab('messages')}
              data-testid="messages-tab"
            >
              💬 Messages
              {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
                <span className="tab-badge">
                  {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
                </span>
              )}
            </button>
            <button
              className={`main-tab ${mainTab === 'friends' ? 'active' : ''}`}
              onClick={() => setMainTab('friends')}
              data-testid="friends-tab"
            >
              👥 Friends
              {pendingCount > 0 && <span className="tab-badge request-badge">{pendingCount}</span>}
            </button>
          </div>
        )}

        {/* Messages Tab Content */}
        {mainTab === 'messages' && (
          <div className="tab-content messages-content">
            {loading ? (
              <div className="loading-state">Loading conversations...</div>
            ) : conversations.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">💬</span>
                <p className="empty-title">No messages yet</p>
                <p className="empty-subtitle">Go to Friends tab to find people to chat with!</p>
                <button 
                  className="empty-action-btn"
                  onClick={() => setMainTab('friends')}
                >
                  👥 Find Friends
                </button>
              </div>
            ) : (
              <div className="conversations-list">
                {conversations.map((conv) => (
                  <div key={conv.friend.user_id} className="conversation-item">
                    <div className="conv-clickable" onClick={() => openChat(conv.friend)}>
                      <div className="conv-avatar">{getSafeAvatar(conv.friend.avatar)}</div>
                      <div className="conv-content">
                        <div className="conv-header">
                          <span className="conv-name">{getDisplayName(conv.friend)}</span>
                          <span className="conv-time">{formatTime(conv.last_message_time)}</span>
                        </div>
                        <div className="conv-preview">
                          {sanitizeMessageDisplay(conv.last_message) || 'No messages yet'}
                        </div>
                      </div>
                      {conv.unread_count > 0 && (
                        <div className="unread-badge">{conv.unread_count}</div>
                      )}
                    </div>
                    
                    {deleteConvConfirm === conv.friend.user_id ? (
                      <div className="delete-conv-confirm" onClick={(e) => e.stopPropagation()}>
                        <span>Delete?</span>
                        <button className="confirm-yes" onClick={() => handleDeleteConversation(conv.friend.user_id)}>Yes</button>
                        <button className="confirm-no" onClick={() => setDeleteConvConfirm(null)}>No</button>
                      </div>
                    ) : (
                      <button 
                        className="delete-conv-btn"
                        onClick={(e) => { e.stopPropagation(); setDeleteConvConfirm(conv.friend.user_id); }}
                        title="Delete conversation"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friends Tab Content */}
        {mainTab === 'friends' && (
          <div className="tab-content friends-content">
            {/* Friends Sub-tabs */}
            <div className="friends-sub-tabs" data-testid="friends-sub-tabs">
              <button
                className={`sub-tab ${friendsSubTab === 'list' ? 'active' : ''}`}
                onClick={() => setFriendsSubTab('list')}
              >
                My Friends ({friendsList.length})
              </button>
              <button
                className={`sub-tab ${friendsSubTab === 'requests' ? 'active' : ''}`}
                onClick={() => setFriendsSubTab('requests')}
              >
                Requests
                {pendingCount > 0 && <span className="sub-badge">{pendingCount}</span>}
              </button>
              <button
                className={`sub-tab ${friendsSubTab === 'search' ? 'active' : ''}`}
                onClick={() => setFriendsSubTab('search')}
              >
                🔍 Search
              </button>
            </div>

            {/* Friends List Sub-tab */}
            {friendsSubTab === 'list' && (
              <div className="friends-list-content">
                {loading ? (
                  <div className="loading-state">Loading friends...</div>
                ) : friendsList.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">👥</span>
                    <p className="empty-title">No friends yet</p>
                    <p className="empty-subtitle">Use Search to find and add friends!</p>
                    <button 
                      className="empty-action-btn"
                      onClick={() => setFriendsSubTab('search')}
                    >
                      🔍 Search Users
                    </button>
                  </div>
                ) : (
                  <div className="friends-list-items">
                    {friendsList.map((friend) => (
                      <div key={friend.user_id} className="friend-item" data-testid={`friend-${friend.user_id}`}>
                        <div className="friend-avatar">{getSafeAvatar(friend.avatar)}</div>
                        <div className="friend-info">
                          <span className="friend-name">{getDisplayName(friend)}</span>
                          <span className="friend-status">
                            {friend.is_online ? '🟢 Online' : '⚪ Offline'}
                          </span>
                        </div>
                        <button 
                          className="message-friend-btn"
                          onClick={() => openChat(friend)}
                          title="Send message"
                        >
                          💬
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Requests Sub-tab */}
            {friendsSubTab === 'requests' && (
              <div className="requests-content">
                {/* Received Requests */}
                <div className="requests-section">
                  <h4 className="section-title">
                    📥 Received ({pendingRequests.received.length})
                  </h4>
                  {pendingRequests.received.length === 0 ? (
                    <p className="no-requests">No pending requests</p>
                  ) : (
                    <div className="requests-list">
                      {pendingRequests.received.map((req) => (
                        <div key={req.user_id} className="request-item">
                          <div className="request-avatar">{getSafeAvatar(req.avatar)}</div>
                          <div className="request-info">
                            <span className="request-name">{getDisplayName(req)}</span>
                            <span className="request-email">{req.email}</span>
                          </div>
                          <div className="request-actions">
                            <button
                              className="accept-btn"
                              onClick={() => acceptFriendRequest(req.user_id)}
                              disabled={actionLoading === req.user_id}
                            >
                              {actionLoading === req.user_id ? '...' : '✓'}
                            </button>
                            <button
                              className="decline-btn"
                              onClick={() => declineFriendRequest(req.user_id)}
                              disabled={actionLoading === req.user_id}
                            >
                              {actionLoading === req.user_id ? '...' : '✕'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sent Requests */}
                <div className="requests-section">
                  <h4 className="section-title">
                    📤 Sent ({pendingRequests.sent.length})
                  </h4>
                  {pendingRequests.sent.length === 0 ? (
                    <p className="no-requests">No sent requests</p>
                  ) : (
                    <div className="requests-list">
                      {pendingRequests.sent.map((req) => (
                        <div key={req.user_id} className="request-item sent">
                          <div className="request-avatar">{getSafeAvatar(req.avatar)}</div>
                          <div className="request-info">
                            <span className="request-name">{getDisplayName(req)}</span>
                            <span className="request-status">Pending...</span>
                          </div>
                          <button
                            className="cancel-btn"
                            onClick={() => cancelFriendRequest(req.user_id)}
                            disabled={actionLoading === req.user_id}
                          >
                            {actionLoading === req.user_id ? '...' : 'Cancel'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Search Sub-tab */}
            {friendsSubTab === 'search' && (
              <div className="search-content">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-users-input"
                  />
                  {searching && <span className="search-spinner">⏳</span>}
                </div>
                
                {searchQuery.trim() ? (
                  <div className="search-results">
                    {searchResults.length === 0 ? (
                      <p className="no-results">
                        {searching ? 'Searching...' : 'No users found'}
                      </p>
                    ) : (
                      searchResults.map((searchUser) => {
                        const isFriend = friendsList.some(f => f.user_id === searchUser.user_id);
                        const isPending = pendingRequests.sent.some(r => r.user_id === searchUser.user_id);
                        const isReceived = pendingRequests.received.some(r => r.user_id === searchUser.user_id);
                        
                        return (
                          <div key={searchUser.user_id} className="search-result-item">
                            <div className="result-avatar">{getSafeAvatar(searchUser.avatar)}</div>
                            <div className="result-info">
                              <span className="result-name">{getDisplayName(searchUser)}</span>
                              <span className="result-email">{searchUser.email}</span>
                            </div>
                            {isFriend ? (
                              <button 
                                className="message-btn"
                                onClick={() => openChat(searchUser)}
                              >
                                💬 Message
                              </button>
                            ) : isPending ? (
                              <span className="pending-status">Pending</span>
                            ) : isReceived ? (
                              <button
                                className="accept-request-btn"
                                onClick={() => acceptFriendRequest(searchUser.user_id)}
                                disabled={actionLoading === searchUser.user_id}
                              >
                                {actionLoading === searchUser.user_id ? '...' : 'Accept'}
                              </button>
                            ) : (
                              <button
                                className="add-friend-btn"
                                onClick={() => sendFriendRequest(searchUser.user_id)}
                                disabled={actionLoading === searchUser.user_id}
                              >
                                {actionLoading === searchUser.user_id ? '...' : '+ Add'}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div className="search-hint">
                    <span className="hint-icon">🔍</span>
                    <p>Type a name or email to search for users</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chat View */}
        {mainTab === 'chat' && selectedFriend && (
          <div className="chat-view">
            <div className="chat-messages">
              {loading ? (
                <div className="loading-messages">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="empty-chat">
                  <span className="empty-icon">💬</span>
                  <p>No messages yet</p>
                  <p className="hint">Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const msgKey = `pm-${msg.message_id}`;
                  const isOwnMessage = msg.sender_id === user?.user_id;
                  
                  // First try phrase dictionary translation for received messages
                  const phraseTranslation = !isOwnMessage
                    ? translateMessage(msg.message, language)
                    : { original: msg.message, translated: null };
                  
                  // Check if we have an async AI translation
                  const aiTranslation = asyncTranslations[msgKey];
                  
                  // Use phrase translation first, then AI translation
                  const finalTranslation = phraseTranslation.translated || aiTranslation;
                  
                  // If phrase translation didn't work and we need AI translation, fetch it
                  if (!isOwnMessage && !phraseTranslation.translated && 
                      phraseTranslation.needsAiTranslation && !aiTranslation) {
                    fetchTranslation(msgKey, msg.message);
                  }
                  
                  return (
                    <div
                      key={msg.message_id}
                      className={`message ${isOwnMessage ? 'sent' : 'received'}`}
                    >
                      <div className="message-bubble">
                        <p className="message-text">{sanitizeMessageDisplay(msg.message)}</p>
                        {finalTranslation && finalTranslation.toLowerCase() !== msg.message.toLowerCase() && (
                          <div className="message-translation">
                            <span className="translation-label">🌐</span>
                            <span className="translated-text">{finalTranslation}</span>
                          </div>
                        )}
                        {!finalTranslation && phraseTranslation.sourceLang && phraseTranslation.sourceLang !== language && (
                          <div className="message-translation loading">
                            <span className="translation-label">🌐</span>
                            <span className="translated-text">Translating...</span>
                          </div>
                        )}
                        <div className="message-footer">
                          <span className="message-time">
                            {new Date(msg.created_at).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                          {isOwnMessage && (
                            <button
                              className="delete-message-btn"
                              onClick={() => setDeleteConfirm(msg.message_id)}
                              title="Delete message"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {deleteConfirm === msg.message_id && (
                        <div className="delete-confirm">
                          <span>Delete?</span>
                          <button className="confirm-yes" onClick={() => handleDeleteMessage(msg.message_id)}>Yes</button>
                          <button className="confirm-no" onClick={() => setDeleteConfirm(null)}>No</button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="chat-input-area">
              <input
                type="text"
                className="chat-input"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                disabled={sending}
                data-testid="chat-message-input"
              />
              <button
                className="send-button"
                onClick={sendMessage}
                disabled={!messageInput.trim() || sending}
                data-testid="send-message-btn"
              >
                {sending ? '...' : '➤'}
              </button>
            </div>
          </div>
        )}

        {/* Hidden audio element for notifications */}
        <audio ref={audioRef} preload="auto" />
      </div>
    </div>
  );
};

export default FriendChat;
